"use client";

import { useTranslations } from "next-intl";
import React, { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useMutation } from "@tanstack/react-query";
import IconSpinnerDots from "@/components/icon/spinner/IconSpinnerDots";
import { AnimatedState } from "@/components/flow/AnimatedState";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { MarketRequestForm } from "@/lib/types/resource";
import { Textarea } from "@/components/ui/textarea";
import { onSubmitMarketRequest } from "../../request/actions";
import { toast } from "sonner";
import { CalendarRange, FileChartColumn, MapPin, Plus, X } from "lucide-react";

/** The monthly report a request is sent from, already localized. */
export type MarketRequestReport = {
  id: string;
  title: string;
  type: string;
  month: string;
  place: string;
  /** Prefill for the locations field; empty for national reports. */
  locations: string;
};

type Props = {
  report?: MarketRequestReport;
  /** Replaces the default outline button. */
  trigger?: React.ReactNode;
};

const getDefaultForm = (report?: MarketRequestReport): MarketRequestForm => ({
  title: "",
  description: "",
  locations: report?.locations ?? "",
  email: "",
});

const MarketRequestSheet: React.FC<Props> = ({ report, trigger }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState<MarketRequestForm>(getDefaultForm(report));
  // A report page attaches its report; the reader can send without it.
  const [attached, setAttached] = useState(true);
  const t = useTranslations("market");
  const attachedReport = report && attached ? report : null;

  const { isPending, mutateAsync } = useMutation({
    mutationFn: onSubmitMarketRequest.bind(null, {
      ...form,
      monthlyReportId: attachedReport?.id ?? null,
    }),
  });

  const onSubmitHandler = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      await mutateAsync();

      toast.success(t("request.sheet.alert.success"));
      setForm(getDefaultForm(report));
      setAttached(true);
      setIsOpen(false);
    } catch (error) {
      toast.error(t("request.sheet.alert.error"));
      setIsOpen(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        {trigger ?? (
          <Button variant="outline">{t("request.cta.button")}</Button>
        )}
      </SheetTrigger>
      <SheetContent side="right" className=" w-full sm:w-3/4 overflow-y-scroll">
        <SheetHeader>
          <SheetTitle>
            {report
              ? t("request.sheet.report.title")
              : t("request.sheet.title")}
          </SheetTitle>
          <SheetDescription className="text-xs">
            {report
              ? t("request.sheet.report.description")
              : t("request.sheet.description")}
          </SheetDescription>
        </SheetHeader>
        <form className="flex flex-col gap-4 pt-6" onSubmit={onSubmitHandler}>
          {report &&
            (attachedReport ? (
              <div className="relative flex flex-col gap-3 rounded-2xl border border-border bg-background p-4 shadow-sm">
                <div className="flex min-w-0 gap-3 pr-8">
                  <span className="flex size-10 flex-none items-center justify-center rounded-xl bg-secondary">
                    <FileChartColumn className="size-5" aria-hidden="true" />
                  </span>
                  <div className="flex min-w-0 flex-col gap-1">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {t("request.sheet.report.attached")}
                    </p>
                    <p className="line-clamp-2 text-sm font-semibold leading-snug">
                      {attachedReport.title}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <Badge variant="secondary">{attachedReport.type}</Badge>
                  <Badge variant="outline" className="gap-1">
                    <CalendarRange className="size-3.5" aria-hidden="true" />
                    {attachedReport.month}
                  </Badge>
                  <Badge variant="outline" className="gap-1">
                    <MapPin className="size-3.5" aria-hidden="true" />
                    {attachedReport.place}
                  </Badge>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  className="absolute right-2 top-2"
                  title={t("request.sheet.report.remove")}
                  onClick={() => setAttached(false)}
                >
                  <X />
                  <span className="sr-only">
                    {t("request.sheet.report.remove")}
                  </span>
                </Button>
              </div>
            ) : (
              <div className="flex flex-col gap-2 rounded-2xl border border-dashed border-border p-4">
                <p className="text-xs leading-5 text-muted-foreground">
                  {t("request.sheet.report.detached")}
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-fit"
                  onClick={() => setAttached(true)}
                >
                  <Plus />
                  {t("request.sheet.report.attach")}
                </Button>
              </div>
            ))}

          <FormInputWrapper
            label={t("request.sheet.form.title.label")}
            for="market-title"
            description={t("request.sheet.form.title.description")}
          >
            <Input
              id="market-title"
              placeholder={
                report
                  ? t("request.sheet.report.title-placeholder")
                  : t("request.sheet.form.title.placeholder")
              }
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="placeholder:text-xs"
            />
          </FormInputWrapper>
          <FormInputWrapper
            label={t("request.sheet.form.description.label")}
            for="market-description"
            description={t("request.sheet.form.description.description")}
          >
            <Textarea
              id="market-description"
              placeholder={t("request.sheet.form.description.placeholder")}
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              className="placeholder:text-xs"
              rows={4}
            />
          </FormInputWrapper>

          <FormInputWrapper
            label={t("request.sheet.form.locations.label")}
            for="market-locations"
            description={t("request.sheet.form.locations.description")}
          >
            <Input
              id="market-locations"
              placeholder={t("request.sheet.form.locations.placeholder")}
              value={form.locations}
              onChange={(e) => setForm({ ...form, locations: e.target.value })}
              className="placeholder:text-xs"
            />
          </FormInputWrapper>

          <FormInputWrapper
            label={t("request.sheet.form.email.label")}
            for="market-email"
            description={t("request.sheet.form.email.description")}
          >
            <Input
              id="market-email"
              placeholder={t("request.sheet.form.email.placeholder")}
              value={form.email}
              type="email"
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="placeholder:text-xs"
            />
          </FormInputWrapper>

          <div className="flex flex-row gap-2 items-center justify-around">
            <Button
              variant="outline"
              type="button"
              className="w-full"
              onClick={() => setIsOpen(false)}
            >
              {t("request.sheet.button.cancel")}
            </Button>
            <Button type="submit" className="w-full" disabled={isPending}>
              <AnimatedState>
                {isPending ? (
                  <IconSpinnerDots />
                ) : (
                  t("request.sheet.button.submit")
                )}
              </AnimatedState>
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
};

export default MarketRequestSheet;

type FormInputWrapperProps = {
  label: string;
  for: string;
  description: string;
  children: React.ReactNode;
};

const FormInputWrapper: React.FC<FormInputWrapperProps> = ({
  label,
  description,
  for: htmlFor,
  children,
}) => {
  return (
    <div className="space-y-1">
      <Label className="font-medium" htmlFor={htmlFor}>
        {label}
      </Label>
      {children}
      <p className="text-muted-foreground text-[10px] pt-1 leading-3">
        {description}
      </p>
    </div>
  );
};
